import React, { useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './classes/AdvancedCertificateLeadForm.css';
import { loadCountryStateLists, CANADIAN_PROVINCES } from './services/CountryStateLists';
import { isBlockedEmail } from './services/QuickAppService';
import { DialCode } from './SidebarQuickForm';
import { mountInPlaceOf, isEmail } from './TalkWithLynnForm';
import ReCAPTCHA from 'react-google-recaptcha';
import { configData } from '../config';

// The lead form with the Advanced Certificate pages' classes (<form class="AC-form" id="AC-form">,
// styled by the theme's adv_form.css), which a course body can hold: the Sports Career Strategies
// course (node 319565) has it under its Apply Now heading. On the live site the page's own script
// filled in its state list as the page opened (mob_changeCountry("US"): the United States' states
// under "-Select State/Province-", the country left at "-Select Country-"), and when a country was
// picked listed that country's states for the US and Canada, or swapped the list for the province
// box elsewhere. It also made the phone field an intl-tel-input one. Scripts in a body are not run
// here (Body.js), so this does the same with the form.

const STATE_COUNTRIES = ['US', 'CA'];

// The phone field as the page script's intl-tel-input made it (separateDialCode, the United States
// first, the flag hidden by the theme): a grey box at the field's left end with the country's
// calling code ("+1") and a small arrow, which opens the country list, and the number typed beside
// it. The same box as the quick forms' (SidebarQuickForm's DialCode). The number input keeps the
// body's id, name and placeholder; the full international number goes in a hidden full_phone
// field, as intl-tel-input's hiddenInput put it.
const PhoneField = ({ placeholder, onChange }) => {
    const [value, setValue] = useState();
    return (
        <PhoneInput
            className="ac-form-phone"
            id="fphone"
            name="fphone"
            placeholder={placeholder}
            defaultCountry="US"
            addInternationalOption={false}
            flagComponent={DialCode}
            // The library names the country list after the number ("fphoneCountry"); live's form
            // posted no such field
            countrySelectProps={{ name: undefined }}
            value={value}
            onChange={(phone) => {
                setValue(phone);
                onChange(phone || '');
            }}
        />
    );
};

// Puts the intl-tel-input style phone field in place of the body's #fphone; returns the function
// that gives the body its own input back
const setUpPhoneField = (form) => {
    const input = form.querySelector('#fphone');
    if (!input) return () => {};
    const fullPhone = document.createElement('input');
    fullPhone.type = 'hidden';
    fullPhone.name = 'full_phone';
    const putBack = mountInPlaceOf(input, (
        <PhoneField placeholder={input.getAttribute('placeholder') || ''} onChange={(phone) => { fullPhone.value = phone; }} />
    ));
    form.appendChild(fullPhone);
    return () => {
        fullPhone.remove();
        putBack();
    };
};

// The form's reCAPTCHA: a <div class="g-recaptcha" data-sitekey="…">&nbsp;</div> in the body. The
// Drupal site loaded Google's api.js in its default mode, which draws a widget in every such box by
// itself (replacing the &nbsp;); this site loads it with render=explicit (react-google-recaptcha), so
// nothing drew one and the form showed an empty gap above SUBMIT. The widget is mounted in the box
// here instead; the body's own CSS still scales the box to 77% and gives it its 2% top margin.
// Returns { token(): the visitor's answer, or null until they tick it; reset(); undo() }, where undo
// gives the box its own content back.
const setUpRecaptcha = (form) => {
    const box = form.querySelector('.g-recaptcha');
    if (!box) return { token: () => null, reset: () => {}, undo: () => {} };
    const content = [...box.childNodes];
    const placeholder = document.createElement('div');
    box.replaceChildren(placeholder);
    const widget = React.createRef();
    let token = null;
    let putBack = null;
    let alive = true;
    whenRecaptchaCanDraw(() => {
        if (!alive) return;
        putBack = mountInPlaceOf(placeholder, (
            <ReCAPTCHA
                ref={widget}
                sitekey={configData.GOOGLE_RECAPTCHA_SITE_KEY}
                onChange={(answer) => { token = answer || null; }}
                onExpired={() => { token = null; }}
            />
        ));
    });
    return {
        token: () => token,
        reset: () => {
            token = null;
            widget.current?.reset();
        },
        undo: () => {
            alive = false;
            putBack?.();
            box.replaceChildren(...content);
        },
    };
};

// react-google-recaptcha counts Google's script as loaded once window.grecaptcha exists, but api.js
// makes that object (with only grecaptcha.ready) before its library is in and grecaptcha.render can
// draw. A widget mounted in that gap is never drawn, and on the course page the quick forms' own
// reCAPTCHAs start loading the script just before the body's form is set up, so this one fell in it.
// Mounting waits for the library then; with no script on the page yet, or the library in,
// react-google-recaptcha copes by itself.
const whenRecaptchaCanDraw = (callback) => {
    const { grecaptcha } = window;
    if (!grecaptcha || typeof grecaptcha.render === 'function' || typeof grecaptcha.ready !== 'function') {
        callback();
        return;
    }
    grecaptcha.ready(callback);
};

// The page script's ac_form_validation: the first problem with the form, in its order and words, as
// [selector of the field it put the cursor in, or null; message], or null when there is none
const formProblem = (form, recaptcha) => {
    const value = (name) => form.elements[name]?.value ?? '';
    const email = value('email');
    if (value('fname') === '') return [null, 'Please enter your first name.'];
    if (value('lname') === '') return [null, 'Please enter your last name.'];
    if (!isEmail(email)) return [null, 'Please enter a valid email address.'];
    if (isBlockedEmail(email)) {
        return [null, email.trim().toLowerCase() === 'info@smww.com'
            ? 'Please enter your email address (cannot be info@smww.com)'
            : 'Please enter your email address.'];
    }
    // The number as typed beside the calling code, then the full number intl-tel-input checked
    if (value('fphone') === '') return [null, 'Please enter your phone number.'];
    if (!isValidPhoneNumber(value('full_phone'))) return ['#fphone', 'Please fill in valid primary phone number.'];
    if (value('fteam') === '') return [null, 'Please enter your favorite team.'];
    if (value('fcountry') === '') return [null, 'Please Select Country'];
    // (a state was asked for in the United States only)
    if (value('fcountry') === 'US' && value('fstate') === '') return [null, 'Please Select State'];
    if (!recaptcha.token()) return [null, "Please verify you're not a robot."];
    return null;
};

// SUBMIT. On the live site the page script checked the form (ac_form_validation), then posted its
// fields to the Drupal site's /api/quick_info_lead, which passed them on to the team site's
// smwwdrupal/quickinfolead; that saves the lead (with page 319565's sort flag), adds its follow-up
// task and sends the emails. The script swapped the form for the body's "Thank you for your
// interest!" box (.advanced-certificate-form-wrapper-successmsg) as soon as it posted; here the
// box shows once the lead is saved, and the visitor is told if it was not. Returns the function
// that gives the button its own handler back.
const setUpSubmit = (form, recaptcha) => {
    const button = form.querySelector('#AC-submit');
    if (!button) return () => {};
    // The page script's handler (ac_form_validation) is not on this site: clicking would only
    // raise an error
    const ownHandler = button.getAttribute('onclick');
    button.removeAttribute('onclick');
    let alive = true;

    const showSent = () => {
        const wrapper = form.closest('.advanced-certificate-form-wrapper');
        const sent = wrapper?.parentElement?.querySelector('.advanced-certificate-form-wrapper-successmsg');
        if (!wrapper || !sent) return;
        wrapper.style.display = 'none';
        sent.style.display = 'block';
    };

    const onClick = async () => {
        if (button.disabled) return;
        const problem = formProblem(form, recaptcha);
        if (problem) {
            const [selector, message] = problem;
            if (selector) form.querySelector(selector)?.focus();
            alert(message);
            return;
        }
        const label = button.value;
        button.value = 'Please wait...';
        button.disabled = true;
        // What $('.AC-form').serialize() posted (the fields the backend reads are always sent), the
        // reCAPTCHA's answer and the address the form was filled in at
        const fields = {
            fname: '', lname: '', email: '', fteam: '', fcity: '', fcountry: '', fstate: '', fprovince: '',
            ...Object.fromEntries(new FormData(form)),
            'g-recaptcha-response': recaptcha.token(),
            pageURL: window.location.href.split('#')[0] + '#quick-form',
        };
        let result = null;
        try {
            const response = await fetch(configData.APP_URL + 'smwwdrupal/quickinfolead', {
                method: 'POST',
                body: JSON.stringify(fields),
                headers: { 'Content-Type': 'application/json' },
            });
            result = (await response.json())?.data?.result ?? null;
        } catch (err) {
            console.error(err);
        }
        if (!alive) return;
        button.value = label;
        button.disabled = false;
        if (result === 'user_created') {
            showSent();
            return;
        }
        // A reCAPTCHA answer counts once
        recaptcha.reset();
        alert(result === 'captcha_failed'
            ? "Please verify you're not a robot."
            : 'Your details could not be sent. Please try again.');
    };
    button.addEventListener('click', onClick);

    return () => {
        alive = false;
        button.removeEventListener('click', onClick);
        if (ownHandler !== null) button.setAttribute('onclick', ownHandler);
    };
};

// Sets up an AC-form found in a page body; returns the function that undoes it
export const setUpAdvancedCertificateLeadForm = (form) => {
    const undoPhone = setUpPhoneField(form);
    const undoLists = setUpStateList(form);
    const recaptcha = setUpRecaptcha(form);
    const undoSubmit = setUpSubmit(form, recaptcha);
    return () => {
        undoSubmit();
        recaptcha.undo();
        undoLists();
        undoPhone();
    };
};

// The page script's state list, filled in for the country picked
const setUpStateList = (form) => {
    const find = (selector) => form.querySelector(selector);
    const country = find('#dfcountry');
    const state = find('#dfstate');
    if (!country || !state) return () => {};
    const stateWrapper = find('#state_wrapper');
    const provinceWrapper = find('#province_wrapper');
    const province = find('#fprovince');
    let alive = true;
    let states = [];
    // The country the visitor picked; the script listed the US's states until then
    let picked = null;

    // The page script's own handler (mob_changeCountry) is not on this site: changing the country
    // would only raise an error
    const ownHandler = country.getAttribute('onchange');
    country.removeAttribute('onchange');

    // The page script's mob_changeCountry
    const showCountry = (code) => {
        if (STATE_COUNTRIES.includes(code)) {
            const listed = states.filter((item) => CANADIAN_PROVINCES.includes(item.abbreviation) === (code === 'CA'));
            state.replaceChildren(new Option('-Select State/Province-', ''), ...listed.map((item) => new Option(item.name, item.abbreviation)));
            if (stateWrapper) stateWrapper.style.display = 'block';
            if (provinceWrapper) provinceWrapper.style.display = 'none';
            if (province) province.value = '';
        } else {
            if (provinceWrapper) provinceWrapper.style.display = 'block';
            if (stateWrapper) stateWrapper.style.display = 'none';
            state.value = '';
        }
    };
    const onChange = () => {
        picked = country.value;
        showCountry(picked);
    };

    // At once with its first entry, then with the states once the lists are in
    showCountry('US');
    loadCountryStateLists().then((lists) => {
        if (!alive) return;
        states = lists.states;
        showCountry(picked ?? 'US');
    });
    country.addEventListener('change', onChange);

    return () => {
        alive = false;
        country.removeEventListener('change', onChange);
        if (ownHandler !== null) country.setAttribute('onchange', ownHandler);
    };
};
